<template>
  <div class="account-page">

    <el-card class="account-info-container">
      <div class="account-info__header">

        <el-row>
          <el-col :span="14">
            <span class="el-text el-text--subheading">{{ userName }}</span>
          </el-col>
          <el-col
            :span="10"
            style="text-align: right;">
            <el-button
              type="text"
              @click="signOut" >
              {{ $t('wre-dashboard.account-page.button.sign-out') }}
            </el-button>
          </el-col>
        </el-row>

      </div>
      <el-divider/>
      <div
        class="account-info__body"
        style="padding-top: 23px; padding-bottom: 26px;">

        <el-row>
          <el-col
            :span="14"
            style="padding-top:5px;">
            <span
              class="el-text el-text--caption"
              v-if="isUnlimitedSubscription">
              {{ $t('wre-dashboard.account-page.message.used-unlimited-space', {brandName: brandName}) }}
            </span>
            <span
              class="el-text el-text--caption"
              v-else>
              {{ $t('wre-dashboard.account-page.message.used-space', storageSpaceUsageInfo) }}
            </span>
          </el-col>

        </el-row>

        <div v-if="!isUnlimitedSubscription">
          <el-progress
            class="my-8"
            :percentage="wormStatus.spaceUsedPercent"
            :color="wormStatus.progressColor"/>

          <div v-if="wormStatus.isVisible">
            <el-icon
              :color="wormStatus.dotColor"
              name="i-dot--16"
              style="margin-left: -4px;"
            />

            <span class="el-text el-text--caption">
              {{ wormStatus.text }}
            </span>
          </div>

        </div>
      </div>
      <el-divider/>
      <div class="account-info_bottom">

        <el-row
          type="flex"
          style="align-items: center;">

          <el-col :span="1">
            <el-icon
              :color="sbDotColor"
              name="i-dot--16"
              style="margin-left: -4px;"
            />
          </el-col>

          <el-col :span="14">
            <span
              class="el-text el-text--caption"
              v-if="isActive">
              {{ $t('wre-dashboard.account-page.message.subscription.active', {brandName: brandName}) }}
              <b>{{ expirationDate | date('medium') }}</b>
            </span>
            <span
              class="el-text el-text--caption"
              v-if="isTrial">
              {{ $t('wre-dashboard.account-page.message.subscription.trial', {brandName: brandName}) }}
              <b>{{ expirationDate | date('medium') }}</b>
            </span>
            <span
              class="el-text el-text--caption"
              v-if="isExpired">
              {{ $t('wre-dashboard.account-page.message.subscription.expired', {brandName: brandName}) }}
              <b>{{ expirationDate | date('medium') }}</b>
            </span>

          </el-col>
          <el-col
            :span="9"
            style="text-align: right;">
            <el-button
              native-type="button"
              @click="manageSubscription"
              type="secondary">
              {{ $t('wre-dashboard.account-page.button.manage-subscription') }}
            </el-button>
          </el-col>
        </el-row>

      </div>

    </el-card>

  </div>
</template>

<script>
import { AUTH_LOGOUT } from '@/app/store/action-types';

export default {
  name: 'AccountPage',
  props: {
    used: {
      type: Number,
      default: 0,
      required: true
    },
    total: {
      type: Number,
      default: 0,
      required: true
    },
    indicatorType: {
      type: String,
      default: 'SPACE_LEFT',
      required: false,
      validator: value => value === 'SPACE_LEFT' || value === 'SPACE_USED'
    },
    userName: {
      type: String,
      default: '',
      required: false
    },
    subscriptionType: {
      type: String,
      default: 'ACTIVE',
      required: false,
      validator: value => value === 'ACTIVE' || value === 'EXPIRED' || value === 'TRIAL'
    },
    expirationDate: {
      type: String,
      default: '',
      required: false
    },
    brandName: {
      type: String,
      default: '',
      required: false
    }
  },
  data() {
    return {};
  },
  methods: {
    async signOut() {
      // this.$gtm.push({
      //   event: 'Acronis',
      //   eventCategory: 'WR_account',
      //   eventAction: 'Sign_out',
      //   eventLabel: ''
      // });

      this.$store.dispatch(AUTH_LOGOUT).then(() => {
        this.$router.push('/enterprise/login');
      });
    },
    manageSubscription() {
      // this.$gtm.push({
      //   event: 'Acronis',
      //   eventCategory: 'WR_account',
      //   eventAction: 'Manage_license',
      //   eventLabel: ''
      // });

      window.open(this.$t('account-page.link.manage-subscription'), '_blank');
    }
  },
  computed: {
    storageSpaceUsageInfo() {
      return {
        usedSpace: this.$options.filters.size(Math.max(this.used, 0)),
        totalSpace: this.$options.filters.size(Math.max(this.total, 0)),
        spaceFree: this.$options.filters.size(Math.max(this.total - this.used, 0)),
        brandName: this.brandName
      };
    },
    wormStatus() {
      let text;
      let dotColor;
      let progressColor;
      let isVisible;

      let spaceUsedPercent = 0;
      let spaceUsedPercentFp = 0.0;

      if (this.total > 0) {
        spaceUsedPercentFp = (this.used / this.total) * 100;
        spaceUsedPercent = Math.min(Math.max(Math.floor(spaceUsedPercentFp), 0), 100);
      }

      const freeSpace = 100.0 - spaceUsedPercentFp;

      if (freeSpace <= 0.0) {
        text = this.$t(
          'wre-dashboard.account-page.message.worm-status.storage-full',
          { brandName: this.brandName }
        );
        progressColor = 'fixed-danger';
        dotColor = 'fixed-danger';
        isVisible = true;
      } else if (freeSpace > 0.0 && freeSpace < 1.0) {
        text = this.$t(
          'wre-dashboard.account-page.message.worm-status.less-than',
          { brandName: this.brandName, percent: 1 }
        );
        progressColor = 'fixed-warning';
        dotColor = 'fixed-warning';
        isVisible = true;
      } else if (freeSpace >= 1.0 && freeSpace < 10.0) {
        text = this.$t(
          'wre-dashboard.account-page.message.worm-status.less-than',
          { brandName: this.brandName, percent: 10 }
        );
        progressColor = 'fixed-warning';
        dotColor = 'fixed-warning';
        isVisible = true;
      } else if (freeSpace >= 10.0 && freeSpace < 30.0) {
        text = this.$t(
          'wre-dashboard.account-page.message.worm-status.less-than',
          { brandName: this.brandName, percent: 30 }
        );
        progressColor = 'fixed-warning';
        dotColor = 'fixed-warning';
        isVisible = true;
      } else if (freeSpace >= 30.0) {
        text = '';
        progressColor = 'fixed-success-lighter';
        dotColor = 'fixed-success';
        isVisible = false;
      }

      return {
        text: text,
        dotColor: dotColor,
        progressColor: progressColor,
        spaceUsedPercent: spaceUsedPercent,
        isVisible: isVisible
      };
    },
    isUnlimitedSubscription() {
      return this.indicatorType === 'SPACE_USED';
    },
    isActive() {
      return this.subscriptionType === 'ACTIVE';
    },
    isExpired() {
      return this.subscriptionType === 'EXPIRED';
    },
    isTrial() {
      return this.subscriptionType === 'TRIAL';
    },
    sbDotColor() {
      return this.isExpired ? 'fixed-critical' : 'fixed-success';
    }
  }
};
</script>

<style lang="scss" scoped>

  $default-elements-padding: 24px;
  $panel-width: 638px;

  .account-info-container {
    margin-top: 48px;
    width: $panel-width;
    margin-left: auto;
    margin-right: auto;
  }

  .account-info__header {
    height: 79px;
    width: $panel-width;
    padding-left: $default-elements-padding;
    padding-right: $default-elements-padding;
    display: table-cell;
    vertical-align: middle;
  }

  .account-info__body {
    height: 98px;
    width: $panel-width;
    padding-left: $default-elements-padding;
    padding-right: $default-elements-padding;
    display: table-cell;
    vertical-align: middle;
  }

  .account-info_bottom {
    min-height: 79px;
    width: $panel-width;
    padding: $default-elements-padding;
    display: table-cell;
    vertical-align: middle;
  }

</style>
